import { useMemo, useState } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, LabelList, CartesianGrid,
} from 'recharts';
import { formatInTz, timezoneAbbr } from '../../lib/timezones';
import kloudynetLogo from '@assets/image_1777160759976.png';
import { useCustomers } from '../../context/CustomerContext';
import { ExecutiveSummary } from './ExecutiveSummary';
import { GeoThreatMap } from './GeoThreatMap';
import { MitreCoverageMatrix } from './MitreCoverageMatrix';
import { fmtTs } from '../../lib/globalTz';

// ── Shared report data shape (same for Daily and Weekly) ─────────────────────
export interface SeverityRow { severity: string; count: number }
export interface ClassificationRow { category: string; count: number }
export interface TimingRow { severity: string; count: number; meanSeconds: number | null; meanFormatted: string }
export interface TopThreatRow { title: string; count: number }
export interface ForeignAccessRow {
  account: string; riskLevel: string; securityFlags: string;
  totalAttempts: number; countries: number; successRate: number;
  credentialAttacks: number; locationDetails: string[];
}
export interface ActiveIncidentRow {
  incidentNumber: number; title: string; status: string; severity: string;
  owner: string; comments: string; createdTime: string;
}
export interface ReportData {
  customerId: string;
  timezone: string;
  reportDate: string;            // YYYY-MM-DD anchor (period end for weekly)
  reportDateLocal?: string;
  windowStartUtc?: string;
  windowEndUtc?: string;
  generatedAt: string;
  homeCountry: string;
  severity: { total: number; breakdown: SeverityRow[] };
  openIncidents: { total: number; high: number; medium: number; low: number; informational: number; unassigned: number };
  classification: ClassificationRow[];
  triage: TimingRow[];
  mttc: TimingRow[];
  /**
   * Stats for the immediately preceding period (prior day for daily, prior 7
   * days for weekly). Field name retained from the original daily contract for
   * backward compatibility - the KPI strip relabels it via `deltaLabel` below.
   */
  previousDay: { closed: number; active: number; unassigned: number; high: number };
  topThreats: TopThreatRow[];
  foreignAccess: ForeignAccessRow[];
  activeIncidents: ActiveIncidentRow[];
  /**
   * MITRE ATT&CK tactic counts for the period - one entry per tactic
   * observed across incidents created today (mv-expanded, so an incident
   * tagged with multiple tactics is credited to each).
   */
  mitre?: { tactic: string; count: number }[];
}

// ── Reference-report color palette ────────────────────────────────────────────
const C = {
  primary: '#4c81d1',
  primaryDark: '#2c3e50',
  danger: '#dc3545',
  warning: '#fd7e14',
  yellow: '#ffc107',
  success: '#28a745',
  info: '#17a2b8',
  purple: '#6f42c1',
} as const;

const SEV_COLOR: Record<string, string> = {
  High: C.danger,
  Medium: C.warning,
  Low: C.yellow,
  Informational: C.info,
};

const SEV_PILL: Record<string, { bg: string; fg: string }> = {
  High: { bg: '#fee2e2', fg: '#991b1b' },
  Medium: { bg: '#ffedd5', fg: '#9a3412' },
  Low: { bg: '#fef3c7', fg: '#854d0e' },
  Informational: { bg: '#cffafe', fg: '#155e75' },
};

const CLASS_COLOR: Record<string, string> = {
  'True Positive': C.success,
  'Benign Positive': C.yellow,
  'False Positive': C.danger,
  'Undetermined': C.info,
  Other: C.purple,
};

const RISK_PILL: Record<string, { bg: string; fg: string }> = {
  CRITICAL: { bg: C.danger, fg: '#ffffff' },
  'HIGH RISK': { bg: '#ef4444', fg: '#ffffff' },
  HIGH: { bg: '#ef4444', fg: '#ffffff' },
  MODERATE: { bg: C.warning, fg: '#ffffff' },
  'LOW RISK': { bg: C.yellow, fg: '#1f2937' },
  LOW: { bg: C.yellow, fg: '#1f2937' },
  MINIMAL: { bg: '#f1f5f9', fg: '#64748b' },
};

// ── Date helpers ──────────────────────────────────────────────────────────────
/**
 * Format a YYYY-MM-DD calendar date (already in the customer's TZ) as a long
 * weekday-prefixed string. Anchors at noon UTC so DST doesn't roll the date.
 */
export function formatLongDate(localYmd: string): string {
  const [y, m, d] = localYmd.split('-').map(Number);
  if (!y || !m || !d) return localYmd;
  const anchor = new Date(Date.UTC(y, m - 1, d, 12));
  return fmtTs(anchor, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * Directional KPI delta indicator.
 * Arrow direction always matches the sign of (curr - prev).
 * Color reflects whether the change is "good" or "bad" for the given metric:
 *   goodDirection: 'up'   → growth is good (e.g. incidents closed)
 *   goodDirection: 'down' → growth is bad  (e.g. active, unassigned, high-open)
 */
function trendArrow(curr: number, prev: number, goodDirection: 'up' | 'down', deltaWord: string) {
  const diff = curr - prev;
  if (diff === 0) return <span style={{ color: '#666' }} aria-label={`no change vs ${deltaWord}`}>0</span>;
  const isGood = (diff > 0 && goodDirection === 'up') || (diff < 0 && goodDirection === 'down');
  const color = isGood ? C.success : C.danger;
  const arrow = diff > 0 ? '▲' : '▼';
  const sign = diff > 0 ? `+${diff}` : `${diff}`;
  const label = `${diff > 0 ? 'up' : 'down'} ${Math.abs(diff)} vs ${deltaWord}`;
  return <span style={{ color }} aria-label={label}>{arrow} {sign}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public component
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportViewProps {
  report: ReportData;
  customerName: string;
  /** Title shown in the header banner ("Daily Security Intelligence Report" etc). */
  title: string;
  /** Right-hand side of the timestamp line ("Report Period: …"). */
  periodLine: string;
  /** Word used in KPI delta labels: "prior day" / "prior week". */
  deltaWord: string;
  /** Short label shown under each KPI value. */
  closedLabel: string;
}

export function ReportView({
  report,
  customerName,
  title,
  periodLine,
  deltaWord,
  closedLabel,
}: ReportViewProps) {
  const reportTz = report.timezone;
  const { activeCustomer } = useCustomers();
  const tzAbbr = timezoneAbbr(reportTz);

  const severityPieData = useMemo(
    () => report.severity.breakdown
      .filter((r) => r.count > 0)
      .map((r) => ({ name: r.severity, value: r.count, color: SEV_COLOR[r.severity] ?? '#94a3b8' })),
    [report],
  );

  const activeBreakdown = useMemo(() => {
    const o = report.openIncidents;
    return [
      { name: 'High', value: o.high, color: SEV_COLOR.High },
      { name: 'Medium', value: o.medium, color: SEV_COLOR.Medium },
      { name: 'Low', value: o.low, color: SEV_COLOR.Low },
      { name: 'Informational', value: o.informational, color: SEV_COLOR.Informational },
    ];
  }, [report]);
  const activeBreakdownNonZero = activeBreakdown.filter((d) => d.value > 0);

  const classChartData = useMemo(
    () => report.classification.map((r, i) => ({
      name: r.category,
      value: r.count,
      color: CLASS_COLOR[r.category] ?? [C.success, C.yellow, C.danger, C.info, C.purple][i % 5],
    })),
    [report],
  );

  const topThreatChartData = useMemo(
    () => report.topThreats.slice().reverse().map((r) => ({
      name: r.title.length > 60 ? r.title.slice(0, 57) + '…' : r.title,
      fullName: r.title,
      value: r.count,
    })),
    [report],
  );

  // Derive the country aggregate for the GeoThreatMap from the foreign-access
  // rows. We split each row's totalAttempts evenly across the countries it
  // touches and keep fractional shares (the map's aggregator handles them
  // and only filters out true zeros).
  const geoMapData = useMemo(() => {
    const acc = new Map<string, number>();
    for (const row of report.foreignAccess) {
      const countries = new Set<string>();
      for (const loc of (row.locationDetails ?? [])) {
        // "City, CC" → "CC"; fall back to the whole string if there's no comma.
        const parts = String(loc).split(',');
        const tail = (parts[parts.length - 1] ?? '').trim();
        if (tail) countries.add(tail);
      }
      if (countries.size === 0) continue;
      const share = row.totalAttempts / countries.size;
      for (const c of countries) acc.set(c, (acc.get(c) ?? 0) + share);
    }
    return Array.from(acc, ([country, count]) => ({ country, count }));
  }, [report.foreignAccess]);

  return (
    <>
      {/* ── Header banner ─────────────────────────────────────────────── */}
      <header
        style={{
          background: `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
          color: '#fff',
          padding: '28px 32px',
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          marginBottom: 24,
        }}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="rounded-lg bg-white px-4 py-2.5">
            <img src={kloudynetLogo} alt="Kloudynet" style={{ height: 44, display: 'block' }} />
          </div>
          <div className="flex-1 text-center min-w-[240px]">
            <h1 style={{ fontSize: '2.1em', fontWeight: 300, marginBottom: 6, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
              {title}
            </h1>
            <p style={{ opacity: 0.92, fontSize: '1.05em' }}>{customerName}</p>
          </div>
          {activeCustomer?.logoUrl && (
            <div className="rounded-lg bg-white px-4 py-2.5">
              <img src={activeCustomer.logoUrl} alt={customerName} style={{ height: 44, display: 'block' }} />
            </div>
          )}
        </div>
      </header>

      <div className="text-center text-slate-500 italic mb-6 text-sm">
        {periodLine} ({tzAbbr})
        &nbsp;|&nbsp;
        Generated: {formatInTz(report.generatedAt, reportTz, {
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZoneName: 'short',
        })}
      </div>

      <ExecutiveSummary period="daily" snapshot={report} cacheKey={report.generatedAt} />

      {/* ── KPI strip ─────────────────────────────────────────────────── */}
      <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <KpiCard
          tone="success"
          value={report.severity.total}
          label={closedLabel}
          deltaWord={deltaWord}
          trend={trendArrow(report.severity.total, report.previousDay.closed, 'up', deltaWord)}
        />
        <KpiCard
          tone="danger"
          value={report.openIncidents.total}
          label="Active Incidents"
          deltaWord={deltaWord}
          trend={trendArrow(report.openIncidents.total, report.previousDay.active, 'down', deltaWord)}
        />
        <KpiCard
          tone="warning"
          value={report.openIncidents.unassigned}
          label="Unassigned Incidents"
          deltaWord={deltaWord}
          trend={trendArrow(report.openIncidents.unassigned, report.previousDay.unassigned, 'down', deltaWord)}
        />
        <KpiCard
          tone="primary"
          value={report.openIncidents.high}
          label="High Severity (Active)"
          deltaWord={deltaWord}
          trend={trendArrow(report.openIncidents.high, report.previousDay.high, 'down', deltaWord)}
        />
      </div>

      {/* ── Incident Analysis (severity pie + active pie) ─────────────── */}
      <section className="dr-section">
        <div className="dr-section-header">Incident Analysis</div>
        <div className="dr-section-content">
          <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <PieBlock
              title="Resolved Incidents by Severity"
              rows={(report.severity.breakdown.length
                ? report.severity.breakdown
                : [{ severity: 'High', count: 0 }, { severity: 'Medium', count: 0 }, { severity: 'Low', count: 0 }, { severity: 'Informational', count: 0 }]
              ).map((r) => ({ label: r.severity, count: r.count, color: SEV_COLOR[r.severity] ?? '#94a3b8' }))}
              pieData={severityPieData}
              emptyLabel="No closed incidents in this window."
            />
            <PieBlock
              title="Active Incidents Breakdown"
              rows={activeBreakdown.map((d) => ({ label: d.name, count: d.value, color: d.color }))}
              pieData={activeBreakdownNonZero}
              emptyLabel="No active incidents."
            />
          </div>
        </div>
      </section>

      {/* ── Threat Classification ─────────────────────────────────────── */}
      <section className="dr-section">
        <div className="dr-section-header">Threat Classification Analysis</div>
        <div className="dr-section-content">
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(260px, 1fr) 2fr' }}>
            <div>
              <table className="dr-table">
                <thead>
                  <tr><th>Classification</th><th style={{ textAlign: 'center', width: 90 }}>Count</th></tr>
                </thead>
                <tbody>
                  {report.classification.length === 0 && (
                    <tr><td colSpan={2} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>No classifications.</td></tr>
                  )}
                  {report.classification.map((c) => (
                    <tr key={c.category}>
                      <td>{c.category}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="dr-count-badge">{c.count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{ height: 320 }}
              role="img"
              aria-label={`Classification distribution bar chart: ${classChartData.map((d) => `${d.name} ${d.value}`).join(', ') || 'no data'}`}
            >
              {classChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classChartData} margin={{ top: 30, right: 20, left: 10, bottom: 30 }}>
                    <CartesianGrid stroke="#e0e0e0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(76,129,209,0.08)' }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {classChartData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      <LabelList dataKey="value" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#1f2937' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-400 italic">No data.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Response Time Analytics (avg triage + avg close) ─────────────── */}
      <section className="dr-section">
        <div className="dr-section-header">Response Time Analytics</div>
        <div className="dr-section-content">
          <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <TimingPanel
              title="Average Time to First Response"
              bg="#f0fdf4" border="#d1fae5" titleColor="#00695c" valueColor="#00695c"
              rows={report.triage} valueLabel="Avg time"
            />
            <TimingPanel
              title="Average Time to Close"
              bg="#eff6ff" border="#bfdbfe" titleColor="#1565c0" valueColor="#1565c0"
              rows={report.mttc} valueLabel="Avg time"
            />
          </div>
        </div>
      </section>

      {/* ── Top 5 Threat Vectors ──────────────────────────────────────── */}
      <section className="dr-section">
        <div className="dr-section-header danger">Top 5 Threat Vectors</div>
        <div className="dr-section-content">
          {report.topThreats.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-8">No incidents to rank for this window.</div>
          ) : (
            <>
              <div
                style={{ height: Math.max(260, topThreatChartData.length * 56) }}
                role="img"
                aria-label={`Top 5 threat vectors horizontal bar chart: ${report.topThreats.map((t) => `${t.title} ${t.count}`).join('; ')}`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topThreatChartData} layout="vertical" margin={{ top: 10, right: 40, left: 20, bottom: 10 }}>
                    <CartesianGrid stroke="#e0e0e0" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
                    <YAxis
                      type="category" dataKey="name" width={340}
                      tick={{ fontSize: 11, fill: '#1f2937' }}
                    />
                    <Tooltip
                      formatter={(v: number) => [v, 'Incidents']}
                      labelFormatter={(_, payload) => (payload?.[0]?.payload as { fullName?: string })?.fullName ?? ''}
                    />
                    <Bar dataKey="value" fill={C.danger} radius={[0, 6, 6, 0]}>
                      <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 700, fill: '#1f2937' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="dr-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr><th>Threat Type</th><th style={{ textAlign: 'center', width: 110 }}>Frequency</th></tr>
                </thead>
                <tbody>
                  {report.topThreats.map((t) => (
                    <tr key={t.title}>
                      <td>{t.title}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="dr-count-badge danger">{t.count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </section>

      {/* ── MITRE ATT&CK Coverage Matrix ──────────────────────────────── */}
      <section className="dr-section" style={{ background: 'transparent', boxShadow: 'none', padding: 0, margin: '0 0 24px' }}>
        <MitreCoverageMatrix
          title="MITRE ATT&CK Coverage (Today)"
          periodLabel="today"
          data={report.mitre ?? []}
        />
      </section>

      {/* ── Geographic Threat Map ─────────────────────────────────────── */}
      {/* Per-account foreign-access rows give us locationDetails strings of
          the form "City, CountryCode". We extract every distinct country
          mentioned and credit it with the row's totalAttempts evenly split
          across the row's countries - an even split is the best we can do
          without per-country breakdowns from the underlying KQL. We keep
          fractional shares so that low-volume rows aren't lost to rounding
          before the map's aggregation step. */}
      <section className="dr-section" style={{ background: 'transparent', boxShadow: 'none', padding: 0, margin: '0 0 24px' }}>
        <GeoThreatMap
          title="Threat Origin Map (Today)"
          subtitle="Distinct countries observed in foreign sign-in attempts."
          homeCountry={report.homeCountry}
          data={geoMapData}
        />
      </section>

      {/* ── Top 10 Foreign Access ─────────────────────────────────────── */}
      <section className="dr-section">
        <div className="dr-section-header orange">Top 10 Foreign Access Activity</div>
        <div className="dr-section-content">
          <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: 10, border: '1px solid #e0e0e0', marginBottom: 18 }}>
            <h4 style={{ color: C.primaryDark, margin: '0 0 10px 0', fontSize: '0.95em', fontWeight: 600 }}>
              Risk Level Classification Criteria
            </h4>
            <table className="dr-table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Risk Level</th>
                  <th>Criteria (any condition met triggers the level)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><RiskBadge level="CRITICAL" /></td><td>Security alerts &gt; 10 &nbsp;|&nbsp; Credential attacks &gt; 20 &nbsp;|&nbsp; Countries &gt; 10</td></tr>
                <tr><td><RiskBadge level="HIGH RISK" /></td><td>Security alerts &gt; 5 &nbsp;|&nbsp; Credential attacks &gt; 10 &nbsp;|&nbsp; Countries &gt; 5</td></tr>
                <tr><td><RiskBadge level="MODERATE" /></td><td>Security alerts &gt; 2 &nbsp;|&nbsp; Credential attacks &gt; 5 &nbsp;|&nbsp; Countries &gt; 3</td></tr>
                <tr><td><RiskBadge level="LOW RISK" /></td><td>Security alerts &gt; 0 &nbsp;|&nbsp; Credential attacks &gt; 0 &nbsp;|&nbsp; Countries &gt; 1</td></tr>
                <tr><td><RiskBadge level="MINIMAL" /></td><td>No security alerts, no credential attacks, single country access</td></tr>
              </tbody>
            </table>
          </div>

          {report.foreignAccess.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-6">
              No foreign sign-ins detected outside {report.homeCountry}.
            </div>
          ) : (
            <table className="dr-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th style={{ textAlign: 'center' }}>Risk Level</th>
                  <th>Security Flags</th>
                  <th style={{ textAlign: 'center' }}>Attempts</th>
                  <th style={{ textAlign: 'center' }}>Countries</th>
                  <th style={{ textAlign: 'center' }}>Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {report.foreignAccess.map((r) => (
                  <tr key={r.account}>
                    <td style={{ fontSize: 11, wordBreak: 'break-all', maxWidth: 140 }}>{r.account}</td>
                    <td style={{ textAlign: 'center' }}><RiskBadge level={r.riskLevel} /></td>
                    <td style={{ fontSize: 11, color: '#475569', maxWidth: 220 }}>{r.securityFlags || 'No Flags'}</td>
                    <td style={{ textAlign: 'center' }}><strong>{r.totalAttempts}</strong></td>
                    <td style={{ textAlign: 'center' }}>{r.countries}</td>
                    <td style={{ textAlign: 'center' }}>{r.successRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ── Active Incidents Details ──────────────────────────────────── */}
      <section className="dr-section">
        <div className="dr-section-header danger">Active Incidents Details</div>
        <div className="dr-section-content">
          {report.activeIncidents.length === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-6">
              No incidents from this window are still open.
            </div>
          ) : (
            <>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                <h4 style={{ color: '#9a3412', margin: 0, fontSize: '0.95em', fontWeight: 700 }}>Requires Attention</h4>
                <p style={{ color: '#9a3412', margin: '4px 0 0 0', fontSize: 13 }}>
                  The following incidents are currently active and may require immediate action.
                </p>
              </div>
              <table className="dr-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Severity</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {report.activeIncidents.map((r) => (
                    <tr key={r.incidentNumber}>
                      <td style={{ whiteSpace: 'nowrap' }}><strong>#{r.incidentNumber}</strong></td>
                      <td style={{ fontSize: 12, maxWidth: 280, wordBreak: 'break-word' }}>{r.title}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="dr-status-active">{r.status || 'Active'}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}><SeverityPill severity={r.severity} /></td>
                      <td style={{ fontSize: 12, maxWidth: 160, wordBreak: 'break-word' }}>{r.owner || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <ReportFooter generatedAt={report.generatedAt} timezone={reportTz} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared report footer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Standardised footer rendered at the bottom of every report (Daily, Weekly,
 * Monthly, Yearly). Centralised here so branding and the generated-at line
 * stay consistent across surfaces - including the HTML export, which clones
 * the rendered DOM verbatim.
 *
 * `generatedAt` is an ISO string; `timezone` is an IANA zone (e.g.
 * `Asia/Kuala_Lumpur`). The footer renders the timestamp in the customer's
 * local timezone so the footer reads correctly regardless of the analyst's
 * own browser timezone.
 */
export function ReportFooter({ generatedAt, timezone }: { generatedAt: string; timezone: string }) {
  return (
    <footer style={{ textAlign: 'center', color: '#64748b', padding: '24px 0 8px' }}>
      <img
        src={kloudynetLogo}
        alt="Kloudynet"
        style={{ height: 36, opacity: 0.85, marginBottom: 4, display: 'inline-block' }}
      />
      <p style={{ fontSize: 10, opacity: 0.7, margin: '0 0 8px' }}>
        Property of Kloudynet &copy; {new Date().getFullYear()}
      </p>
      <p style={{ fontWeight: 700, color: C.primaryDark, letterSpacing: 1, margin: '4px 0' }}>
        SECURITY OPERATIONS CENTER
      </p>
      <p style={{ fontSize: 13, margin: '2px 0' }}>
        Powered by Kloudynet Advanced Threat Detection
      </p>
      <p style={{ marginTop: 12, fontSize: 12 }}>
        Report generated on {formatInTz(generatedAt, timezone, {
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZoneName: 'short',
        })}
      </p>
      <p style={{ fontSize: 11, opacity: 0.7 }}>
        This report contains confidential security information • Distribution restricted to authorized personnel
      </p>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared shell (toolbar + style block + loading/error states)
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportShellProps {
  /** Title shown in the (no-print) toolbar above the report. */
  toolbarTitle: string;
  customerName: string;
  /** Date-picker controls injected by the parent (Daily uses 1 input, Weekly 1, etc). */
  controls: React.ReactNode;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
  /** Disabled state for the export buttons - usually `!report`. */
  printDisabled: boolean;
  /**
   * Builder for a downloadable HTML snapshot - a DOM clone with all page CSS
   * inlined (see `lib/exportDomReport.ts`), guaranteed to look pixel-identical
   * to the live view. When omitted, no HTML button renders.
   */
  buildHtml?: () => Promise<{ ok: boolean; fileName?: string; error?: string }>
                  | { ok: boolean; fileName?: string; error?: string };
  /**
   * Builder for the downloadable PDF - the DOM-clone-then-print approach
   * from `lib/exportDomReport.ts` (`exportDomAsPdf`), same clone as
   * `buildHtml`. When omitted, no PDF button renders.
   */
  buildPdf?: () => Promise<{ ok: boolean; fileName?: string; error?: string }>
                 | { ok: boolean; fileName?: string; error?: string };
  /** Override the default "Download HTML" button text. */
  htmlLabel?: string;
  /** Override the default "Building HTML…" busy text. */
  htmlBuildingLabel?: string;
  /** Override the default "Download PDF" button text (e.g. "Download HTML"). */
  exportLabel?: string;
  /** Override the default "Building PDF…" busy text. */
  exportBuildingLabel?: string;
  /** Optional extra controls rendered to the LEFT of the export button(s). */
  extraToolbarActions?: React.ReactNode;
}

export function ReportShell({
  toolbarTitle, customerName, controls, loading, error, children, printDisabled,
  buildHtml, buildPdf, htmlLabel, htmlBuildingLabel, exportLabel, exportBuildingLabel, extraToolbarActions,
}: ReportShellProps) {
  const labelHtmlDownload = htmlLabel ?? 'Download HTML';
  const labelHtmlBuilding = htmlBuildingLabel ?? 'Building HTML…';
  const labelDownload = exportLabel ?? 'Download PDF';
  const labelBuilding = exportBuildingLabel ?? 'Building PDF…';
  // ── Export state machine. Tracks which of the (up to two) export buttons
  //    is in flight so HTML and PDF export can't race each other and each
  //    shows its own busy/error state independently.
  const [exportState, setExportState] = useState<
    | { kind: 'idle' }
    | { kind: 'busy'; which: 'html' | 'pdf' }
    | { kind: 'error'; which: 'html' | 'pdf'; message: string }
  >({ kind: 'idle' });

  const runExport = async (
    which: 'html' | 'pdf',
    builder: () => Promise<{ ok: boolean; fileName?: string; error?: string }> | { ok: boolean; fileName?: string; error?: string },
  ) => {
    if (printDisabled) return;
    setExportState({ kind: 'busy', which });
    try {
      const result = await builder();
      if (result.ok) {
        setExportState({ kind: 'idle' });
      } else {
        setExportState({ kind: 'error', which, message: result.error ?? 'Export failed.' });
        window.setTimeout(() => setExportState({ kind: 'idle' }), 6000);
      }
    } catch (e) {
      setExportState({
        kind: 'error',
        which,
        message: e instanceof Error ? e.message : 'Export failed.',
      });
      window.setTimeout(() => setExportState({ kind: 'idle' }), 6000);
    }
  };

  const handleExportHtml = () => buildHtml && runExport('html', buildHtml);
  const handleExportPdf = () => buildPdf && runExport('pdf', buildPdf);

  return (
    <div className="daily-report-root">
      {/* Light-theme report shell - kept identical between Daily and Weekly so the
          printed/exported HTML structure matches the reference Logic-App output. */}
      <style>{`
        .daily-report-root {
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
          padding: 24px;
          border-radius: 16px;
          font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
          color: #333;
        }
        .dr-section { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); margin-bottom: 24px; overflow: hidden; }
        .dr-section-header { background: linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%); color: #fff; padding: 16px 24px; font-size: 1.05em; font-weight: 600; letter-spacing: 0.3px; }
        .dr-section-header.danger { background: linear-gradient(135deg, #7f1d1d 0%, ${C.danger} 100%); }
        .dr-section-header.orange { background: linear-gradient(135deg, #9a3412 0%, ${C.warning} 100%); }
        .dr-section-content { padding: 24px; }
        .dr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .dr-table th { text-align: left; padding: 10px 12px; background: #f8fafc; color: ${C.primaryDark}; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
        .dr-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1f2937; }
        .dr-table tr:last-child td { border-bottom: none; }
        .dr-pill { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.4px; }
        .dr-count-badge { display: inline-block; padding: 4px 12px; background: ${C.primary}; color: #fff; border-radius: 999px; font-weight: 700; font-size: 12px; }
        .dr-count-badge.danger { background: ${C.danger}; }
        .dr-status-active { display: inline-block; padding: 3px 10px; background: #fee2e2; color: #991b1b; border-radius: 999px; font-size: 11px; font-weight: 700; }
        @media print {
          .dr-no-print { display: none !important; }
          .daily-report-root { background: #ffffff; padding: 0; }
          .dr-section { box-shadow: none; border: 1px solid #e5e7eb; page-break-inside: avoid; }
        }
      `}</style>

      {/* Toolbar (no print) */}
      <div className="dr-no-print mb-4 flex flex-wrap items-end gap-3 rounded-xl bg-white/70 dark:bg-slate-800/70 backdrop-blur px-4 py-3 shadow-sm border border-white dark:border-slate-700">
        <div>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{toolbarTitle}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Customer: <span className="text-slate-700 dark:text-slate-200 font-medium">{customerName}</span></p>
        </div>
        <div className="ml-auto flex items-end gap-3">
          {controls}
          {extraToolbarActions}
          {exportState.kind === 'error' && (
            <span
              className="self-center rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 px-2.5 py-1 text-xs text-red-700 dark:text-red-300"
              role="alert"
              title={exportState.message}
            >
              Export failed - try again.
            </span>
          )}
          {buildHtml && (
            <button
              onClick={() => { void handleExportHtml(); }}
              disabled={printDisabled || exportState.kind === 'busy'}
              aria-busy={exportState.kind === 'busy' && exportState.which === 'html'}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-4 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:-translate-y-px hover:bg-slate-50 dark:hover:bg-slate-800"
              title="Download a self-contained HTML snapshot of this report"
            >
              {exportState.kind === 'busy' && exportState.which === 'html' ? (
                <>
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block', width: 12, height: 12,
                      border: '2px solid rgba(100,116,139,0.4)',
                      borderTopColor: 'currentColor', borderRadius: '50%',
                      animation: 'rs-export-spin 0.8s linear infinite',
                    }}
                  />
                  {labelHtmlBuilding}
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {labelHtmlDownload}
                </>
              )}
            </button>
          )}
          {buildPdf && (
            <button
              onClick={() => { void handleExportPdf(); }}
              disabled={printDisabled || exportState.kind === 'busy'}
              aria-busy={exportState.kind === 'busy' && exportState.which === 'pdf'}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:-translate-y-px"
              style={{
                background: exportState.kind === 'busy' && exportState.which === 'pdf'
                  ? '#475569'
                  : `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
                boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
              }}
              title="Download a branded PDF of this report"
            >
              {exportState.kind === 'busy' && exportState.which === 'pdf' ? (
                <>
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block', width: 12, height: 12,
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'rs-export-spin 0.8s linear infinite',
                    }}
                  />
                  {labelBuilding}
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {labelDownload}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes rs-export-spin { to { transform: rotate(360deg); } }`}</style>

      {loading && (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-600 shadow">
          <div className="text-sm">Querying the customer's Microsoft Sentinel workspace…</div>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">{error}</div>
      )}

      <div>{children}</div>
    </div>
  );
}

// ── Subcomponents (private to ReportView) ────────────────────────────────────

function KpiCard({ tone, value, label, trend, deltaWord }: {
  tone: 'success' | 'danger' | 'warning' | 'primary';
  value: number; label: string; trend: React.ReactNode; deltaWord: string;
}) {
  const accent = tone === 'danger' ? C.danger
    : tone === 'success' ? C.success
    : tone === 'warning' ? C.warning
    : C.primary;
  return (
    <div
      style={{
        background: '#fff', borderRadius: 16, padding: 22,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: 5, height: '100%', background: accent }} />
      <div style={{ fontSize: '2.4em', fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value}</div>
      <div style={{ color: '#64748b', fontSize: '0.92em', marginTop: 6 }}>{label}</div>
      <div style={{ marginTop: 6, fontSize: '0.82em' }}>vs {deltaWord}: {trend}</div>
    </div>
  );
}

function PieBlock({ title, rows, pieData, emptyLabel }: {
  title: string;
  rows: { label: string; count: number; color: string }[];
  pieData: { name: string; value: number; color: string }[];
  emptyLabel: string;
}) {
  return (
    <div style={{ background: '#f9fafb', padding: 22, borderRadius: 12, border: '1px solid #e5e7eb' }}>
      <h3 style={{ color: C.primary, margin: '0 0 16px 0', textAlign: 'center', fontSize: '1.1em', fontWeight: 600 }}>{title}</h3>
      <table className="dr-table" style={{ marginBottom: 16 }}>
        <thead>
          <tr><th>Severity</th><th style={{ textAlign: 'center', width: 80 }}>Count</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td style={{ textAlign: 'center' }}>
                <SeverityPill severity={r.label} fallbackCount={r.count} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        style={{ height: 240 }}
        role="img"
        aria-label={`${title}: ${pieData.length === 0 ? emptyLabel : pieData.map((d) => `${d.name} ${d.value}`).join(', ')}`}
      >
        {pieData.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-slate-400 italic">{emptyLabel}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
                label={(p) => `${p.name} ${Math.round(((p.value as number) / pieData.reduce((s, d) => s + d.value, 0)) * 100)}%`}
                labelLine={false}
                style={{ fontSize: 11 }}
              >
                {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function SeverityPill({ severity, fallbackCount }: { severity: string; fallbackCount?: number }) {
  const style = SEV_PILL[severity];
  if (!style) {
    return <span style={{ color: '#475569' }}>{fallbackCount ?? severity}</span>;
  }
  return (
    <span className="dr-pill" style={{ background: style.bg, color: style.fg }}>
      {fallbackCount ?? severity}
    </span>
  );
}

function RiskBadge({ level }: { level: string }) {
  const key = level?.toUpperCase();
  const style = RISK_PILL[key] ?? RISK_PILL.MINIMAL;
  return (
    <span className="dr-pill" style={{ background: style.bg, color: style.fg }}>{level}</span>
  );
}

function TimingPanel({ title, bg, border, titleColor, valueColor, rows, valueLabel }: {
  title: string; bg: string; border: string; titleColor: string; valueColor: string;
  rows: TimingRow[]; valueLabel: string;
}) {
  return (
    <div style={{ background: bg, padding: 22, borderRadius: 12, border: `1px solid ${border}` }}>
      <h3 style={{ color: titleColor, margin: '0 0 16px 0', textAlign: 'center', fontSize: '1.1em', fontWeight: 600 }}>{title}</h3>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic text-center py-4">No data for this window.</div>
      ) : (
        <table className="dr-table">
          <thead>
            <tr>
              <th>Severity</th>
              <th style={{ textAlign: 'center' }}>Incidents</th>
              <th style={{ textAlign: 'right' }}>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.severity}>
                <td><SeverityPill severity={r.severity} fallbackCount={undefined} /></td>
                <td style={{ textAlign: 'center' }}><strong>{r.count}</strong></td>
                <td style={{ textAlign: 'right', color: valueColor, fontWeight: 600 }}>{r.meanFormatted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
